import React, { useEffect, useState } from 'react';
import { useFlag } from '../../lib/flags';
import './ReadingProgressBar.css';

// Slim coloured bar across the very top of the viewport that fills as the
// user scrolls through an article. Triggers only when:
//   - flag readingProgressBar is on
//   - a target element exists (so the bar tracks the article body, not the
//     whole page; otherwise long footers cause the bar to fill before the
//     reader finishes reading the article).
//
// Pass `targetSelector` from the consuming page; default is window.scrollY
// against documentElement height.

const ReadingProgressBar = ({ targetSelector }) => {
  const enabled = useFlag('readingProgressBar');
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const target = targetSelector ? document.querySelector(targetSelector) : null;
    let raf = 0;

    const measure = () => {
      raf = 0;
      if (target) {
        const rect = target.getBoundingClientRect();
        const total = Math.max(rect.height - window.innerHeight, 1);
        const passed = Math.min(Math.max(-rect.top, 0), total);
        setPct((passed / total) * 100);
      } else {
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        if (total <= 0) { setPct(0); return; }
        setPct(Math.min((window.scrollY / total) * 100, 100));
      }
    };

    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled, targetSelector]);

  if (!enabled) return null;

  return (
    <div className="tn-reading-progress" role="progressbar" aria-label="Reading progress" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className="tn-reading-progress__fill" style={{ transform: `scaleX(${pct / 100})` }} />
    </div>
  );
};

export default ReadingProgressBar;
